import {
    moveToNextLeafBlock,
    moveToPreviousLeafBlock,
    type TableGridView,
    table,
} from '@gitbook/doc-core';
import type { Change } from '@gitbook/slate';

import type { TableViewOnKeyDownFn } from '../types';
import { relativeSelectionMove } from '../utils';
import { deleteRowAndMoveSelection } from './hooks/useTableRowOptions';

/**
 * Wraps a change function to handle both direct Change objects and change functions, as these plugins should support both Slate and non-Slate focusable nodes.
 */
function wrapChange(
    change: Change | ((fn: (change: Change) => any) => void)
): (fn: (change: Change) => any) => void {
    return typeof change === 'function' ? change : (fn) => fn(change);
}

/**
 * Handle keyboard interaction in the view.
 */
export const onKeyDown: TableViewOnKeyDownFn<TableGridView> = (
    key,
    tableBlock,
    tableState,
    change,
    event
) => {
    const wrappedChange = wrapChange(change);

    /** Insert a new row and focus in it */
    const insertNewRow = (definitionId: string | undefined = tableState.selection.definitionId) => {
        if (!definitionId) {
            return;
        }

        wrappedChange((change) => {
            const currentIndex = tableState.records.findIndex(
                (entry) => entry.key === tableState.selection.recordId
            );
            const currentRecordEntry = tableState.records[currentIndex];
            const afterRecordEntry = tableState.records[currentIndex + 1];

            const newRecordId = table.insertEmptyRecord(change, tableBlock.key, {
                before: currentRecordEntry.value.orderIndex,
                after: afterRecordEntry?.value.orderIndex,
            });

            table.moveTableSelection(
                change,
                tableBlock,
                {
                    recordId: newRecordId,
                    definitionId,
                },
                'start'
            );
        });
    };

    const isShiftModifier = event?.shiftKey;

    // Override the key if the key event is from a non-Slate node. We do this as we can't detect shift+tab with a single `case` in below switch statement for non-Slate keyboard events.
    if (isShiftModifier && event.key === 'Tab') {
        key = 'shift+tab';
    }

    // Shortcut to delete the row that currently has the selection
    if (key === 'mod+-') {
        wrappedChange((change) => {
            if (!tableState.selection.recordId) {
                // If no record is selected, do nothing
                return;
            }

            deleteRowAndMoveSelection(
                change,
                tableBlock.key,
                tableState,
                tableState.selection.recordId
            );
        });
    }

    switch (key) {
        //
        // Move to preview/next row
        //
        case 'ArrowUp':
        case 'move-up':
        case 'ArrowDown':
        case 'move-down': {
            wrappedChange((change) => {
                if (
                    !relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { relative: 0 },
                        record: { relative: key === 'move-up' ? -1 : 1 },
                        focus: key === 'move-up' ? 'end' : 'start',
                    })
                ) {
                    // Otherwise move outside the table
                    if (key === 'move-up') {
                        moveToPreviousLeafBlock(change, tableBlock);
                    } else {
                        moveToNextLeafBlock(change, tableBlock);
                    }
                }
            });
            break;
        }

        //
        // Move to previous/next cell; or next/previous row if none
        //
        case 'Tab':
        case 'tab':
        case 'ArrowRight':
        case 'move-right': {
            wrappedChange((change) => {
                const focus = key === 'tab' ? 'select' : 'start';
                // Move to the right
                if (
                    !relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { relative: 1 },
                        record: { relative: 0 },
                        focus,
                    })
                ) {
                    // Move down if can't
                    if (
                        !relativeSelectionMove(change, tableBlock, tableState, {
                            definition: { absolute: 0 },
                            record: { relative: 1 },
                            focus,
                        }) &&
                        key === 'tab'
                    ) {
                        // Insert new row if tab
                        insertNewRow(tableState.view.columns[0]);
                    }
                }
            });
            break;
        }

        // Shift+Tab on non-Slate Keyboard level is supported by the early `key` override, see line 65.
        case 'ArrowLeft':
        case 'shift+tab':
        case 'move-left': {
            wrappedChange((change) => {
                const focus = key === 'shift+tab' ? 'select' : 'end';
                if (
                    !relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { relative: -1 },
                        record: { relative: 0 },
                        focus,
                    })
                ) {
                    relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { absolute: tableState.view.columns.length - 1 },
                        record: { relative: -1 },
                        focus,
                    });
                }
            });
            break;
        }

        //
        // Pressing backspace moves to the previous column or
        // remove the row if it's the first column of an empty row
        //
        case 'backspace': {
            wrappedChange((change) => {
                if (
                    relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { relative: -1 },
                        record: { relative: 0 },
                        focus: 'end',
                    })
                ) {
                    return;
                }

                // Delete row if pressing backspace at start of empty row
                if (
                    tableState.selection.recordId &&
                    table.isRowEmpty(tableBlock, tableState.selection.recordId)
                ) {
                    relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { absolute: tableState.view.columns.length - 1 },
                        record: { relative: -1 },
                        focus: 'end',
                    });
                    table.removeRecord(change, tableBlock.key, tableState.selection.recordId);
                }

                // Otherwise move to previous row
                else {
                    relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { absolute: tableState.view.columns.length - 1 },
                        record: { relative: -1 },
                        focus: 'end',
                    });
                }
            });
            break;
        }
    }
};
